import { faTimes } from '@fortawesome/pro-light-svg-icons';
import {
  faCaretDown,
  faCaretUp,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Node } from '@react-types/shared';
import { useEffect, useRef } from 'react';
import {
  AriaSelectOptions,
  HiddenSelect,
  useButton,
  useSelect,
} from 'react-aria';
import mergeRefs from 'react-merge-refs';
import { Item, useSelectState } from 'react-stately';
import { RectReadOnly } from 'react-use-measure';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { FormFieldProps } from 'src/components/form/form-field-props';
import { useFormMode } from 'src/components/form/form-mode-context';
import { InnerAddon } from 'src/components/form/inner-addon';
import { Label } from 'src/components/form/label';
import { SelectOption } from 'src/components/form/select';
import { ListBox } from 'src/components/form/select/components/list-box';
import { Popover } from 'src/components/form/select/components/popover';
import { RemoveButton } from 'src/components/form/select/components/remove-button';
import { SelectToggle } from 'src/components/form/select/components/select-toggle';
import { Separator } from 'src/components/form/select/components/separator';
import { applySortBy, SortByOptions } from 'src/components/form/select/utils';
import { borderWidth, placeholderStyle } from 'src/components/form/styles';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Stack } from 'src/components/layout/stack';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Hint } from 'src/components/text/hint';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useFloating } from 'src/hooks/use-floating';
import { useId } from 'src/hooks/use-id';
import { useOnChange } from 'src/hooks/use-on-change';
import { Colors } from 'src/styles';
import styled, { css } from 'styled-components';

export { Item, Section } from 'react-stately';

const ToggleButton = styled.button<{
  error?: boolean;
  leftBounds?: RectReadOnly;
  rightBounds?: RectReadOnly;
}>`
  appearance: none;
  text-align: left;
  background: transparent;
  white-space: nowrap;
  text-overflow: clip;
  margin: 0;
  display: inline-flex;
  overflow: hidden;
  cursor: default;
  align-items: center;
  justify-content: space-between;
  /* mirror SimpleInput style: */
  width: 100%;
  border: ${borderWidth} solid ${Colors.brand};
  color: ${Colors.brand};
  padding-top: 0.6rem;
  padding-bottom: 0.7rem;
  padding-left: ${({ leftBounds }) =>
    leftBounds ? leftBounds.width + 'px' : '2rem'};
  padding-right: ${({ rightBounds }) =>
    rightBounds ? rightBounds.width + 'px' : '2rem'};

  ${({ error }) =>
    error &&
    css`
      border-color: ${Colors.error};
      background-color: ${Colors.errorLight};
      color: ${Colors.error};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      border-color: ${Colors.inactive};
      background: ${Colors.inactiveLight};
      cursor: not-allowed;
      color: ${Colors.inactive};
    `}
`;

const Placeholder = styled.span<{ error?: boolean; disabled?: boolean }>`
  ${placeholderStyle}

  ${({ error }) =>
    error &&
    css`
      border-color: ${Colors.error};
      background-color: ${Colors.errorLight};
      color: ${Colors.error};
    `}

    ${({ disabled }) =>
    disabled &&
    css`
      border-color: ${Colors.inactive};
      background: ${Colors.inactiveLight};
      cursor: not-allowed;
      color: ${Colors.inactive};
    `}
`;

type SingleSelectDropdownProps<Value extends string | null = string> = {
  id?: string;
  name: string;
  showError?: boolean;
  label?: string;
  placeholder?: string;
  options: SelectOption<Value>[];
  dataTestId?: string;
  error?: boolean;
  disabled?: boolean;
  onChange?: (value: Value) => void;
  value: string;
  sortBy?: SortByOptions;
};

export function SingleSelectDropdown<Value extends string | null = string>({
  id,
  label,
  name,
  showError,
  dataTestId,
  error,
  disabled,
  value,
  onChange,
  options,
  placeholder = 'Please Select',
  sortBy = 'label',
}: SingleSelectDropdownProps<Value>) {
  const items = applySortBy(sortBy, options);
  const singleOption = items.length === 1 ? items[0] : false;
  const supportsEmpty = items[0]?.value === ''; // when ordered empty value would be the first one

  const isDisabled = Boolean(singleOption) || disabled;

  const selectStateProps: AriaSelectOptions<SelectOption<Value>> = {
    isDisabled,
    validationState: showError ? 'invalid' : 'valid',
    children: (item) => (
      <Item
        key={item.value}
        textValue={item.label}
        data-testid={`${dataTestId ?? name}-option-${item.value}`}
      >
        {item.label}
      </Item>
    ),
    items,
    label,
    placeholder,
    disabledKeys: items
      .filter((item) => item.disabled)
      .map((item) => item.value ?? ''),
  };

  const state = useSelectState<SelectOption<Value>>(selectStateProps);

  // attention: selectedItem can actually be undefined.
  // the react-aria typings are not correct.
  const selectedItem = state.selectedItem as
    | Node<SelectOption<Value>>
    | undefined;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const { triggerProps, valueProps, menuProps } = useSelect(
    selectStateProps,
    state,
    triggerRef
  );

  const { buttonProps } = useButton(
    {
      ...triggerProps,
      id,
      isDisabled,
    },
    triggerRef
  );

  const { setSelectedKey, close } = state;

  useEffect(() => {
    if (!selectedItem && singleOption) {
      setSelectedKey(singleOption.value ?? '');
    } else if (!selectedItem) {
      setSelectedKey(value);
    } else if (selectedItem.value.value !== value) {
      setSelectedKey(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, singleOption]);

  useOnChange(
    () => {
      if (!selectedItem) return;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (selectedItem.value.value === undefined) return;
      if (selectedItem.value.value !== value) {
        onChange?.(selectedItem.value.value);
      }
    },
    selectedItem?.value.value
  );

  // prefer label of empty value as placeholder
  const chosenPlaceholder = supportsEmpty ? items[0]?.label : placeholder;

  const mode = useFormMode();
  const isForm = mode === 'regular';

  const focusRef = useFocusManager();

  const opened = state.isOpen && !isDisabled && !singleOption;

  const floating = useFloating({ placement: 'bottom-start' });

  return (
    <div>
      <InnerAddon
        right={
          <Stack flow="column" gap={0.5}>
            {supportsEmpty &&
              items.length >= 1 &&
              selectedItem?.value.value &&
              isForm && (
                <RemoveButton
                  error={error}
                  tabIndex={-1}
                  disabled={disabled}
                  data-testid={`${name}-remove-selection`}
                  onClick={() => setSelectedKey('')}
                  style={{ pointerEvents: 'initial' }}
                >
                  <Stack flow="column" gap={0.5}>
                    <FontAwesomeIcon icon={faTimes} />
                    <Separator
                      orientation="vertical"
                      error={error}
                      disabled={isDisabled}
                    />
                  </Stack>
                </RemoveButton>
              )}

            <SelectToggle error={error} disabled={disabled}>
              <FontAwesomeIcon icon={state.isOpen ? faCaretUp : faCaretDown} />
            </SelectToggle>
          </Stack>
        }
      >
        {(addonBounds) => (
          <>
            {/*
              hide select on test to avoid double labels. we need the hidden select only for
              browser form autofill.
            */}
            {process.env.NODE_ENV === 'test' ? null : (
              <HiddenSelect
                state={state}
                triggerRef={triggerRef}
                label={label}
              />
            )}

            {/*
              you can't pass a testid to HiddenSelect currently.
              but by using <input type="hidden"/> we can still access the value easily in tests
              and the user should not notice this.
            */}
            <input
              type="hidden"
              data-testid={dataTestId ?? name}
              value={value}
            />

            <ToggleButton
              {...buttonProps}
              ref={mergeRefs([
                floating.refs.setReference,
                focusRef,
                triggerRef,
              ])}
              error={error}
              data-testid={`${dataTestId ?? name}-togglebutton`}
              data-test-error={showError}
              {...addonBounds}
            >
              <span
                {...valueProps}
                style={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                <p style={{ overflow: 'hidden' }}>
                  {selectedItem?.value.value ? (
                    selectedItem.rendered
                  ) : (
                    <Placeholder error={error} disabled={isDisabled}>
                      {chosenPlaceholder}
                    </Placeholder>
                  )}
                </p>
              </span>
            </ToggleButton>
          </>
        )}
      </InnerAddon>

      {opened && (
        <Popover
          onClose={close}
          ref={floating.refs.setFloating}
          style={{
            zIndex: 1,
            ...floating.style,
            width: floating.refs.domReference.current?.scrollWidth,
          }}
        >
          <ListBox name={name} ariaListBoxProps={menuProps} state={state} />
        </Popover>
      )}
    </div>
  );
}

type SingleSelectProps<Value extends string | null = string> = {
  hideLabel?: boolean;
  stacked?: boolean;
  inline?: boolean;
  options: SelectOption<Value>[];
  disabled?: boolean;
  'data-testid'?: string;
  onChange?: (value: Value) => void;
  placeholder?: string;
  sortBy?: SortByOptions;
} & FormFieldProps;

export function SingleSelect<Value extends string | null = string>(
  props: SingleSelectProps<Value>
) {
  const { minTablet } = useBreakpoints();
  const {
    name,
    label,
    onChange,
    placeholder,
    stacked = !minTablet,
    inline,
    'data-testid': dataTestId,
    options,
    hideLabel = false,
    disabled,
    hint,
    info,
    sortBy = 'label',
    markAsRequired,
  } = props;

  const [field, showError, error, helper] = useCustomField<string | null>(
    name,
    label
  );

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const id = useId();
  const fieldId = formGroup?.id || id;
  const displayedLabel = label + (markAsRequired ? '*' : '');

  const fieldItem = (
    <FieldItem>
      <SingleSelectDropdown
        id={fieldId}
        dataTestId={dataTestId}
        options={options}
        showError={showError}
        label={label}
        placeholder={placeholder}
        error={showError}
        disabled={disabled}
        {...field}
        onChange={(value) => {
          helper.setValue(value);
          onChange?.(value);
        }}
        // if value is null, default to empty string
        value={field.value ?? ''}
        sortBy={sortBy}
      />
      {hint && (
        <Hint disabled={props.disabled} mode="formInput" children={hint} />
      )}
      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
          type="selectable"
        />
      )}
    </FieldItem>
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked} inline={inline}>
      <Label
        htmlFor={fieldId}
        error={showError}
        data-testid={`${field.name}Label`}
      >
        {displayedLabel}

        {info && (
          <>
            &nbsp;
            <Tooltip content={info}>
              {(targetProps) => (
                <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
              )}
            </Tooltip>
          </>
        )}
      </Label>

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
}
